import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useMessagingUnread } from '../context/MessagingUnreadContext';
import { useSocket } from '../context/SocketContext';
import { messagingAPI } from '../services/api';
import { messagingFocusRef } from '../utils/messagingFocusRef';

function sameId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

export function useMessagingInbox(options = {}) {
  const { enabled = true, filterConversation = null } = options;
  const { user } = useAuth();
  const { subscribeMessages } = useSocket();
  const { syncUnreadFromConversations } = useMessagingUnread();

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);

  const selectedConvIdRef = useRef(null);
  const loadConversationsRef = useRef(async () => {});

  useEffect(() => {
    selectedConvIdRef.current = selectedConvId;
  }, [selectedConvId]);

  useEffect(() => {
    messagingFocusRef.messagingTabActive = !!enabled;
    messagingFocusRef.currentConversationId = enabled ? selectedConvId : null;
  }, [enabled, selectedConvId]);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await messagingAPI.listConversations();
      const list = data.conversations || [];
      setConversations(list);
      syncUnreadFromConversations(list);
    } catch (err) {
      console.error('Load conversations failed:', err);
    } finally {
      setLoading(false);
    }
  }, [syncUnreadFromConversations]);

  useEffect(() => {
    loadConversationsRef.current = loadConversations;
  }, [loadConversations]);

  const loadMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const { data } = await messagingAPI.getMessages(convId);
      setMessages(data.messages || []);
      await messagingAPI.markRead(convId).catch(() => {});
      setConversations((prev) => {
        const next = prev.map((c) => (sameId(c._id, convId) ? { ...c, unread: 0 } : c));
        queueMicrotask(() => syncUnreadFromConversations(next));
        return next;
      });
    } catch (err) {
      console.error('Load messages failed:', err);
    }
  }, [syncUnreadFromConversations]);

  useEffect(() => {
    if (!enabled) return;
    loadConversations();
  }, [enabled, loadConversations]);

  useEffect(() => {
    if (!enabled || !selectedConvId) return;
    loadMessages(selectedConvId);
  }, [enabled, selectedConvId, loadMessages]);

  useEffect(() => {
    if (!user?._id) return undefined;
    return subscribeMessages((payload) => {
      const { conversationId, message: msg } = payload;
      if (!msg) return;
      const fromMe = sameId(msg.sender?._id, user._id);

      setConversations((prev) => {
        const idx = prev.findIndex((c) => sameId(c._id, conversationId));
        if (idx === -1) {
          loadConversationsRef.current?.();
          return prev;
        }
        const next = [...prev];
        const cur = next[idx];
        const viewing = sameId(selectedConvIdRef.current, conversationId);
        let unread = cur.unread || 0;
        if (!fromMe && !viewing) unread += 1;
        if (viewing || fromMe) unread = fromMe ? cur.unread || 0 : 0;

        const updated = {
          ...cur,
          lastMessage: { text: msg.text },
          lastMessageAt: msg.createdAt,
          unread
        };
        next.splice(idx, 1);
        next.unshift(updated);
        queueMicrotask(() => syncUnreadFromConversations(next));
        return next;
      });

      if (sameId(selectedConvIdRef.current, conversationId)) {
        setMessages((prev) => {
          if (prev.some((m) => sameId(m._id, msg._id))) return prev;
          return [
            ...prev,
            {
              _id: msg._id,
              text: msg.text,
              at: msg.createdAt,
              fromMe,
              sender: msg.sender
            }
          ];
        });
        if (!fromMe) {
          messagingAPI.markRead(conversationId).catch(() => {});
        }
      }
    });
  }, [user?._id, subscribeMessages, syncUnreadFromConversations]);

  const sendMessage = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!selectedConvId || !messageText.trim()) return;
      try {
        await messagingAPI.sendMessage(selectedConvId, { text: messageText.trim() });
        setMessageText('');
        await loadMessages(selectedConvId);
        await loadConversations();
      } catch (err) {
        console.error('Send failed:', err);
      }
    },
    [selectedConvId, messageText, loadMessages, loadConversations]
  );

  const startConversation = useCallback(
    async (participantId, extra = {}) => {
      if (!participantId) return;
      try {
        const { data } = await messagingAPI.createOrGet({
          participantId,
          ...extra
        });
        const conv = data.conversation;
        if (!conv) return;
        setConversations((prev) => {
          const rest = prev.filter((c) => !sameId(c._id, conv._id));
          return [conv, ...rest];
        });
        setSelectedConvId(conv._id);
        await loadMessages(conv._id);
      } catch (err) {
        console.error('Open conversation failed:', err);
      }
    },
    [loadMessages]
  );

  const displayed =
    typeof filterConversation === 'function'
      ? conversations.filter(filterConversation)
      : conversations;

  return {
    conversations: displayed,
    messages,
    selectedConvId,
    setSelectedConvId,
    messageText,
    setMessageText,
    sendMessage,
    startConversation,
    loading,
    refreshConversations: loadConversations
  };
}
